/**
 * StaffListPage — `/admin/staff` internal-employee list.
 *
 * Matches the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` StaffPage):
 *
 *   - Header: eyebrow "INTERNAL ADMIN" + H1 "Staff" + subtitle
 *     "{N} internal employees · platform-scoped access" + "+ Add staff"
 *     (gated on `staff:invite`).
 *   - A single `Card` holding the table — NO filter chips / search
 *     (the artboard staff list has none; the surface is small).
 *   - Desktop table (≥ 900 px): PERSON (Avatar + name + mono email) |
 *     ROLE (StaffRoleChip) | STATUS | SCOPE | LAST ACTIVE | chevron.
 *   - Mobile card-rows (< 900 px).
 *
 * Earlier this surface was built to `STAFF_SPEC.md` (Role/Status/Region
 * filter chips + search, no Scope column). That spec had drifted from
 * the artboard; the artboard + reviewed screenshots are the source of
 * truth, so the filters are removed and the Scope column is added.
 *
 * Data source (SCRUM-131): `useStaffList()` over `GET /admin/staff`
 * (root + support). The list page maps each `StaffMember` to a
 * `StaffRow`. The Scope + Last active columns have NO backend source,
 * so they render an honest "—" placeholder (decision-4) rather than
 * fabricating data. Tests drive the populated table via `staffOverride`.
 *
 * `StaffStatus` keeps `'invited'` for forward-compat (no rows produce
 * it yet — the BE pending-invitation list endpoint is a follow-up).
 *
 * Permission gates:
 *   `staff:read`   — view list (page-level gate; root + support)
 *   `staff:invite` — render the "+ Add staff" CTA + empty-state CTA
 *                    (now root-only)
 *
 * `StaffStatusBadge` (Tier 0, this file) + `formatRelativeTime` (pure,
 * imported by the Imports surfaces) are exported and unchanged.
 */

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Avatar, Button, Card, Icon } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import { type PlatformRole } from '../../../shared/auth/admin-permissions.js';
import { StaffRoleChip } from '../_components/StaffRoleChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';
import {
  useStaffList,
  useStaffInvitations,
  useResendInvitation,
  useRevokeInvitation,
  type StaffMember,
  type StaffInvitation,
  type ResendInvitationError,
  type RevokeInvitationError,
} from '../api/index.js';

// ── Types ──────────────────────────────────────────────────────

export type StaffStatus = 'active' | 'suspended' | 'invited' | 'expired';

/**
 * Honest placeholder for any column with no backend source (Scope,
 * Last active). Per decision-4 the columns stay but render an em-dash
 * rather than fabricated data.
 */
const NO_BACKEND_SOURCE = '—';

export interface StaffRow {
  readonly id: string;
  readonly name: string;
  readonly email: string;
  readonly role: PlatformRole;
  readonly status: StaffStatus;
}

/** Map a `StaffMember` view-model into a list row. */
function toStaffRow(member: StaffMember): StaffRow {
  return {
    id: member.id,
    name: member.name,
    email: member.email,
    role: member.role,
    status: member.status,
  };
}

// ── StaffStatusBadge (Tier 0) ──────────────────────────────────

const STAFF_STATUS_VISUAL: Record<
  StaffStatus,
  { label: string; background: string; foreground: string; border: string }
> = {
  active: {
    label: 'Active',
    background:
      'color-mix(in oklab, var(--success-500) 14%, var(--bg-surface))',
    foreground: 'var(--success-700)',
    border: 'color-mix(in oklab, var(--success-500) 32%, transparent)',
  },
  suspended: {
    label: 'Suspended',
    background:
      'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
    foreground: 'var(--danger-700)',
    border: 'color-mix(in oklab, var(--danger-500) 30%, transparent)',
  },
  invited: {
    label: 'Invited',
    background:
      'color-mix(in oklab, var(--warning-500) 14%, var(--bg-surface))',
    foreground: 'var(--warning-700)',
    border: 'color-mix(in oklab, var(--warning-500) 32%, transparent)',
  },
  // Expired: a muted/neutral tone — an expired invite is no longer
  // active but is not an error; resend re-arms it (decision-e).
  expired: {
    label: 'Expired',
    background: 'var(--bg-sunken)',
    foreground: 'var(--fg-tertiary)',
    border: 'var(--border-default)',
  },
};

/**
 * Tier 0 module-local status pill for staff (Active / Suspended /
 * Invited). Visual style mirrors `SystemStatusBadge`. `<span>`
 * placeholder until kit `<Badge>` (PR #143) ships.
 */
export function StaffStatusBadge({
  status,
}: {
  readonly status: StaffStatus;
}): React.ReactElement {
  const visual = STAFF_STATUS_VISUAL[status];
  return (
    <span
      role="status"
      aria-label={`Status: ${visual.label}`}
      className="inline-flex items-center font-medium"
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-full)',
        background: visual.background,
        color: visual.foreground,
        border: `1px solid ${visual.border}`,
        fontSize: '12px',
        whiteSpace: 'nowrap',
      }}
    >
      {visual.label}
    </span>
  );
}

// ── Relative time helper ───────────────────────────────────────

/**
 * Phase-1 relative-time formatter — produces "12m ago", "1h ago",
 * "3d ago", "—" for null. Pure, exported so the spec can pin its
 * adversarial input classes + the Imports surfaces can reuse it. A
 * future kit `<RelativeTime>` primitive will replace it.
 */
export function formatRelativeTime(
  iso: string | null,
  now: number = Date.now(),
): string {
  if (iso === null) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const diff = now - t;
  if (diff < 0) return 'just now';
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

// ── Page ───────────────────────────────────────────────────────

export interface StaffListPageProps {
  /** matchMedia override for tests. */
  readonly compact?: boolean;
  /** Test/Storybook override for the stub `useStaff` data. */
  readonly staffOverride?: readonly StaffRow[];
  /**
   * Test/Storybook override for the pending-invitations source. When
   * provided the live `useStaffInvitations()` query result is ignored
   * (mirrors `staffOverride`). `undefined` → derive from the query.
   */
  readonly invitationsOverride?: readonly StaffInvitation[];
}

export function StaffListPage(
  props: StaffListPageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission permission="staff:read" fallback={<NotPermittedPanel />}>
      <StaffListBody {...props} />
    </IfAdminPermission>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <section
      aria-labelledby="staff-list-not-permitted-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="staff-list-not-permitted-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Not permitted
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        You don't have access to view the Staff list. Ask a platform
        administrator if you need this access.
      </p>
    </section>
  );
}

function LoadingPanel(): React.ReactElement {
  return (
    <section
      aria-busy="true"
      aria-live="polite"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <p className="text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Loading staff…
      </p>
    </section>
  );
}

function ErrorPanel({
  onRetry,
}: {
  readonly onRetry: () => void;
}): React.ReactElement {
  return (
    <section
      aria-labelledby="staff-list-error-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="staff-list-error-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Couldn't load staff
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        Something went wrong loading the staff list. Try again.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button type="button" variant="secondary" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </section>
  );
}

// ── Body ───────────────────────────────────────────────────────

function StaffListBody({
  staffOverride,
  invitationsOverride,
  compact: compactOverride,
}: StaffListPageProps): React.ReactElement {
  const compact = useCompactMode(compactOverride);
  const list = useStaffList();
  const invitationsQuery = useStaffInvitations();

  // WCAG 4.1.3 Status Messages: outcomes of the resend/revoke mutations
  // are announced to assistive tech without moving focus. `success` →
  // polite status region; `error` → assertive alert. Cleared per action.
  const [announcement, setAnnouncement] = React.useState<
    { readonly kind: 'success' | 'error'; readonly message: string } | null
  >(null);

  // Tests / Storybook inject rows directly; otherwise derive from the
  // live query's discriminated union.
  if (staffOverride === undefined) {
    if (list.status === 'loading') return <LoadingPanel />;
    if (list.status === 'forbidden') return <NotPermittedPanel />;
    if (list.status === 'error') {
      return <ErrorPanel onRetry={list.refetch} />;
    }
  }

  const staff: readonly StaffRow[] =
    staffOverride ??
    (list.status === 'ready' ? list.members.map(toStaffRow) : []);

  // Pending-invitations source. Override wins (tests/Storybook). The
  // live query's `forbidden` state omits the section (support still
  // sees the staff list); `error` surfaces a small inline notice; an
  // empty list omits the section entirely.
  const invitations: readonly StaffInvitation[] | null =
    invitationsOverride ??
    (invitationsQuery.status === 'ready'
      ? invitationsQuery.invitations
      : null);
  const invitationsErrored =
    invitationsOverride === undefined && invitationsQuery.status === 'error';

  return (
    <section
      aria-labelledby="staff-list-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1280px', padding: compact ? '24px 16px 80px' : '32px 48px 80px' }}
    >
      {/* Header */}
      <div
        className="flex flex-wrap items-end justify-between"
        style={{ gap: '16px', marginBottom: '24px' }}
      >
        <div>
          <p
            className="m-0 font-bold uppercase"
            style={{
              fontSize: '11px',
              letterSpacing: '1.2px',
              color: 'var(--fg-tertiary)',
              marginBottom: '6px',
            }}
          >
            Internal admin
          </p>
          <h1
            id="staff-list-heading"
            className="m-0 font-semibold"
            style={{
              fontSize: '28px',
              letterSpacing: '-0.4px',
              color: 'var(--fg-primary)',
            }}
          >
            Staff
          </h1>
          <p
            className="m-0"
            style={{
              fontSize: '14px',
              color: 'var(--fg-secondary)',
              marginTop: '6px',
            }}
          >
            {staff.length} internal employee{staff.length === 1 ? '' : 's'} ·
            platform-scoped access
          </p>
        </div>
        <IfAdminPermission permission="staff:invite">
          <Button asChild variant="primary">
            <Link to="/admin/staff/new">+ Add staff</Link>
          </Button>
        </IfAdminPermission>
      </div>

      {/* Action announcement (WCAG 4.1.3 Status Messages) for
          resend/revoke outcomes. AT registers live regions at MOUNT
          time, so BOTH regions are PERSISTENT (always in the DOM, even
          when empty); the message is routed into the matching one by
          kind. The polite `status` region carries successes; the
          assertive `alert` region carries errors. Visible colored text
          lives on the inner element so the empty containers stay
          invisible. Labels are invitation-specific to disambiguate from
          the StaffDetail announcer for screen-reader users. */}
      <InvitationAnnouncer announcement={announcement} />

      {/* List */}
      {staff.length === 0 ? (
        <EmptyAll />
      ) : compact ? (
        <StaffMobileList rows={staff} />
      ) : (
        <StaffDesktopTable rows={staff} />
      )}

      {/* Pending invitations — a SEPARATE labeled section below the
          staff table (distinct identity: invitationId ≠ userId). */}
      <PendingInvitationsSection
        invitations={invitations}
        errored={invitationsErrored}
        compact={compact}
        onAnnounce={setAnnouncement}
      />
    </section>
  );
}

// ── Action announcer (WCAG 4.1.3, pre-mounted live regions) ────

/** Discriminated announcement payload shared by the action handlers. */
type Announcement = {
  readonly kind: 'success' | 'error';
  readonly message: string;
} | null;

/**
 * Two always-present sibling live regions. AT registers live regions at
 * mount; injecting a node that already contains text is NOT announced.
 * Pre-mounting both an assertive `alert` and a polite `status` region and
 * routing the message into the matching one by kind keeps the politeness
 * distinction while guaranteeing the announcement fires. Labels are
 * invitation-specific so screen readers can disambiguate this region from
 * the StaffDetail announcer.
 */
function InvitationAnnouncer({
  announcement,
}: {
  readonly announcement: Announcement;
}): React.ReactElement {
  const isError = announcement?.kind === 'error';
  const isSuccess = announcement?.kind === 'success';
  return (
    <>
      <div
        role="status"
        aria-live="polite"
        aria-label="Invitation action result"
      >
        {isSuccess ? (
          <p
            className="m-0"
            style={{
              marginBottom: '14px',
              fontSize: '13px',
              color: 'var(--fg-secondary)',
            }}
          >
            {announcement?.message}
          </p>
        ) : null}
      </div>
      <div
        role="alert"
        aria-live="assertive"
        aria-label="Invitation action error"
      >
        {isError ? (
          <p
            className="m-0"
            style={{
              marginBottom: '14px',
              fontSize: '13px',
              color: 'var(--fg-danger)',
            }}
          >
            {announcement?.message}
          </p>
        ) : null}
      </div>
    </>
  );
}

// ── Pending invitations section (FE-3) ─────────────────────────

const INVITE_ERROR_COPY = {
  forbidden: "You don't have permission to perform this action.",
  resendNotPending:
    "This invitation can't be resent because it's no longer pending.",
  revokeAlreadyAccepted:
    "This invitation has already been accepted and can't be revoked.",
  notFound: "That invitation no longer exists.",
  generic: "Couldn't complete that action. Try again.",
} as const;

function PendingInvitationsSection({
  invitations,
  errored,
  compact,
  onAnnounce,
}: {
  readonly invitations: readonly StaffInvitation[] | null;
  readonly errored: boolean;
  readonly compact: boolean;
  readonly onAnnounce: (
    a: { readonly kind: 'success' | 'error'; readonly message: string } | null,
  ) => void;
}): React.ReactElement | null {
  const resend = useResendInvitation();
  const revoke = useRevokeInvitation();
  // Track which invitation row currently has an in-flight mutation so
  // its action buttons disable (avoid double-submit).
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  // Query error → a small inline non-blocking notice (the staff list
  // above is unaffected). `forbidden` and empty → omit the section.
  if (errored) {
    return (
      <p
        role="status"
        className="m-0"
        style={{
          marginTop: '24px',
          fontSize: '13px',
          color: 'var(--fg-tertiary)',
        }}
      >
        Couldn't load pending invitations. Try refreshing.
      </p>
    );
  }
  if (invitations === null || invitations.length === 0) return null;

  const handleResend = (id: string): void => {
    onAnnounce(null);
    setPendingId(id);
    void resend
      .mutateAsync(id)
      .then(() => {
        onAnnounce({ kind: 'success', message: 'Invitation resent.' });
      })
      .catch((error: ResendInvitationError) => {
        onAnnounce({ kind: 'error', message: resendErrorCopy(error) });
      })
      .finally(() => {
        setPendingId(null);
      });
  };

  const handleRevoke = (id: string): void => {
    onAnnounce(null);
    setPendingId(id);
    void revoke
      .mutateAsync(id)
      .then(() => {
        onAnnounce({ kind: 'success', message: 'Invitation revoked.' });
      })
      .catch((error: RevokeInvitationError) => {
        onAnnounce({ kind: 'error', message: revokeErrorCopy(error) });
      })
      .finally(() => {
        setPendingId(null);
      });
  };

  return (
    <section
      aria-labelledby="pending-invitations-heading"
      style={{ marginTop: '32px' }}
    >
      <h2
        id="pending-invitations-heading"
        className="m-0 font-semibold"
        style={{
          fontSize: '18px',
          letterSpacing: '-0.2px',
          color: 'var(--fg-primary)',
          marginBottom: '4px',
        }}
      >
        Pending invitations
      </h2>
      <p
        className="m-0"
        style={{
          fontSize: '13px',
          color: 'var(--fg-tertiary)',
          marginBottom: '16px',
        }}
      >
        {invitations.length} open invitation
        {invitations.length === 1 ? '' : 's'}
      </p>
      {compact ? (
        <InvitationMobileList
          invitations={invitations}
          pendingId={pendingId}
          onResend={handleResend}
          onRevoke={handleRevoke}
        />
      ) : (
        <InvitationDesktopTable
          invitations={invitations}
          pendingId={pendingId}
          onResend={handleResend}
          onRevoke={handleRevoke}
        />
      )}
    </section>
  );
}

function resendErrorCopy(error: ResendInvitationError): string {
  switch (error.kind) {
    case 'forbidden':
      return INVITE_ERROR_COPY.forbidden;
    case 'notPending':
      return INVITE_ERROR_COPY.resendNotPending;
    case 'notFound':
      return INVITE_ERROR_COPY.notFound;
    default:
      return INVITE_ERROR_COPY.generic;
  }
}

function revokeErrorCopy(error: RevokeInvitationError): string {
  switch (error.kind) {
    case 'forbidden':
      return INVITE_ERROR_COPY.forbidden;
    case 'alreadyAccepted':
      return INVITE_ERROR_COPY.revokeAlreadyAccepted;
    case 'notFound':
      return INVITE_ERROR_COPY.notFound;
    default:
      return INVITE_ERROR_COPY.generic;
  }
}

// ── Invitation action buttons (root-only gate) ─────────────────

function InvitationActions({
  invitation,
  pendingId,
  onResend,
  onRevoke,
  compact = false,
}: {
  readonly invitation: StaffInvitation;
  readonly pendingId: string | null;
  readonly onResend: (id: string) => void;
  readonly onRevoke: (id: string) => void;
  /** In compact/touch contexts use h-12 (48 px) to clear the 44 px floor. */
  readonly compact?: boolean;
}): React.ReactElement {
  const busy = pendingId === invitation.id;
  // ≥44 px touch floor: lg (h-12 = 48 px) in compact/mobile, md
  // (h-10 = 40 px) for the dense desktop table.
  const size = compact ? 'lg' : 'md';
  return (
    <IfAdminPermission permission="staff:manage_invitations">
      <div className="flex items-center" style={{ gap: '8px' }}>
        <Button
          type="button"
          variant="secondary"
          size={size}
          disabled={busy}
          aria-label={`Resend invitation to ${invitation.email}`}
          onClick={() => {
            onResend(invitation.id);
          }}
        >
          <Icon name="refresh" size={14} aria-hidden />
          Resend
        </Button>
        <Button
          type="button"
          variant="secondary"
          size={size}
          disabled={busy}
          aria-label={`Revoke invitation to ${invitation.email}`}
          onClick={() => {
            onRevoke(invitation.id);
          }}
        >
          <Icon name="close" size={14} aria-hidden />
          Revoke
        </Button>
      </div>
    </IfAdminPermission>
  );
}

// ── Invitation desktop table ───────────────────────────────────

function InvitationDesktopTable({
  invitations,
  pendingId,
  onResend,
  onRevoke,
}: {
  readonly invitations: readonly StaffInvitation[];
  readonly pendingId: string | null;
  readonly onResend: (id: string) => void;
  readonly onRevoke: (id: string) => void;
}): React.ReactElement {
  return (
    <Card padding="none">
      <table className="w-full border-collapse" style={{ fontSize: '14px' }}>
        <thead>
          <tr className="text-left font-bold uppercase">
            <th scope="col" style={TH_STYLE}>Email</th>
            <th scope="col" style={TH_STYLE}>Role</th>
            <th scope="col" style={TH_STYLE}>Status</th>
            <th scope="col" style={{ ...TH_STYLE, textAlign: 'right' }}>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {invitations.map((invitation) => (
            <tr
              key={invitation.id}
              style={{ borderTop: '1px solid var(--border-subtle)' }}
            >
              <td
                className="font-mono"
                style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-primary)' }}
              >
                {invitation.email}
              </td>
              <td style={{ padding: '14px 16px' }}>
                <StaffRoleChip role={invitation.role} />
              </td>
              <td style={{ padding: '14px 16px' }}>
                <StaffStatusBadge status={invitation.status} />
              </td>
              <td style={{ padding: '14px 16px' }}>
                <div className="flex justify-end">
                  <InvitationActions
                    invitation={invitation}
                    pendingId={pendingId}
                    onResend={onResend}
                    onRevoke={onRevoke}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ── Invitation mobile list ─────────────────────────────────────

function InvitationMobileList({
  invitations,
  pendingId,
  onResend,
  onRevoke,
}: {
  readonly invitations: readonly StaffInvitation[];
  readonly pendingId: string | null;
  readonly onResend: (id: string) => void;
  readonly onRevoke: (id: string) => void;
}): React.ReactElement {
  return (
    <ul className="m-0 flex list-none flex-col p-0" style={{ gap: '8px' }}>
      {invitations.map((invitation) => (
        <li key={invitation.id}>
          <Card padding="none">
            <div
              className="flex flex-col"
              style={{ padding: '14px', gap: '10px' }}
            >
              <div className="flex items-center" style={{ gap: '6px', flexWrap: 'wrap' }}>
                <StaffRoleChip role={invitation.role} size="sm" />
                <StaffStatusBadge status={invitation.status} />
              </div>
              <span
                className="truncate font-mono"
                style={{ fontSize: '13px', color: 'var(--fg-primary)' }}
              >
                {invitation.email}
              </span>
              <InvitationActions
                invitation={invitation}
                pendingId={pendingId}
                onResend={onResend}
                onRevoke={onRevoke}
                compact
              />
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}

// ── Empty state ────────────────────────────────────────────────

function EmptyAll(): React.ReactElement {
  return (
    <Card padding="lg">
      <p
        className="m-0 text-center"
        style={{ fontSize: '14px', color: 'var(--fg-secondary)' }}
      >
        No staff yet. Add your first.
      </p>
      <IfAdminPermission permission="staff:invite">
        <div className="mt-3 flex justify-center" style={{ marginTop: '12px' }}>
          <Button asChild variant="primary">
            <Link to="/admin/staff/new">+ Add staff</Link>
          </Button>
        </div>
      </IfAdminPermission>
    </Card>
  );
}

// ── Desktop table ──────────────────────────────────────────────

const TH_STYLE: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: '11px',
  letterSpacing: '0.8px',
  color: 'var(--fg-tertiary)',
  background: 'var(--bg-sunken)',
};

function StaffDesktopTable({
  rows,
}: {
  readonly rows: readonly StaffRow[];
}): React.ReactElement {
  return (
    <Card padding="none">
      <table className="w-full border-collapse" style={{ fontSize: '14px' }}>
        <thead>
          <tr className="text-left font-bold uppercase">
            <th scope="col" style={TH_STYLE}>Person</th>
            <th scope="col" style={TH_STYLE}>Role</th>
            <th scope="col" style={TH_STYLE}>Status</th>
            <th scope="col" style={TH_STYLE}>Scope</th>
            <th scope="col" style={TH_STYLE}>Last active</th>
            <th scope="col" style={{ ...TH_STYLE, width: '40px' }}>
              <span className="sr-only">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <StaffDesktopRow key={row.id} row={row} />
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function StaffDesktopRow({
  row,
}: {
  readonly row: StaffRow;
}): React.ReactElement {
  const navigate = useNavigate();
  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
  ): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, [role="link"], [role="button"]') != null) {
      return; // canonical interactive owns the navigation
    }
    navigate(`/admin/staff/${row.id}`);
  };
  return (
    <tr
      data-testid={`staff-row-${row.id}`}
      onClick={handleRowClick}
      className="cursor-pointer hover:bg-[var(--bg-sunken)]"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      <td style={{ padding: '14px 16px' }}>
        <div className="flex items-center" style={{ gap: '10px' }}>
          <Avatar name={row.name} size={32} />
          <div className="flex flex-col" style={{ gap: '2px' }}>
            <Link
              to={`/admin/staff/${row.id}`}
              aria-label={`Open ${row.name}`}
              className="font-semibold no-underline"
              style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
            >
              {row.name}
            </Link>
            <span
              className="font-mono"
              style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
            >
              {row.email}
            </span>
          </div>
        </div>
      </td>
      <td style={{ padding: '14px 16px' }}>
        <StaffRoleChip role={row.role} />
      </td>
      <td style={{ padding: '14px 16px' }}>
        <StaffStatusBadge status={row.status} />
      </td>
      <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-secondary)' }}>
        {NO_BACKEND_SOURCE}
      </td>
      <td style={{ padding: '14px 16px', fontSize: '13px', color: 'var(--fg-tertiary)' }}>
        {NO_BACKEND_SOURCE}
      </td>
      <td style={{ padding: '14px 16px' }}>
        <Icon name="chevRight" size={16} aria-hidden />
      </td>
    </tr>
  );
}

// ── Mobile cards ───────────────────────────────────────────────

function StaffMobileList({
  rows,
}: {
  readonly rows: readonly StaffRow[];
}): React.ReactElement {
  return (
    <div className="flex flex-col" style={{ gap: '8px' }}>
      {rows.map((row) => (
        <StaffMobileCard key={row.id} row={row} />
      ))}
    </div>
  );
}

function StaffMobileCard({
  row,
}: {
  readonly row: StaffRow;
}): React.ReactElement {
  const navigate = useNavigate();
  const handleCardClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, [role="link"], [role="button"]') != null) {
      return;
    }
    navigate(`/admin/staff/${row.id}`);
  };
  return (
    <Card padding="none">
      <div
        data-testid={`staff-mobile-card-${row.id}`}
        onClick={handleCardClick}
        className="flex cursor-pointer items-center justify-between"
        style={{ padding: '14px', gap: '12px', minHeight: '72px' }}
      >
        <div className="flex min-w-0 flex-1 items-center" style={{ gap: '10px' }}>
          <Avatar name={row.name} size={36} />
          <div className="flex min-w-0 flex-col" style={{ gap: '4px' }}>
            <div className="flex items-center" style={{ gap: '6px', flexWrap: 'wrap' }}>
              <StaffRoleChip role={row.role} size="sm" />
              <StaffStatusBadge status={row.status} />
            </div>
            <Link
              to={`/admin/staff/${row.id}`}
              aria-label={`Open ${row.name}`}
              className="truncate font-semibold no-underline"
              style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
            >
              {row.name}
            </Link>
            <span
              className="truncate"
              style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
            >
              {row.email} · {NO_BACKEND_SOURCE} · {NO_BACKEND_SOURCE}
            </span>
          </div>
        </div>
        <Icon name="chevRight" size={16} aria-hidden />
      </div>
    </Card>
  );
}
